import { Image } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useFileByGuid } from "@/hooks/useFileByGuid";
import { cn } from "@/lib/utils";
import { isImageFileName } from "@/lib/utils/file";

interface InlineImagePreviewProps {
  guid: string;
  fileName: string;
  className?: string;
}

export const InlineImagePreview: React.FC<InlineImagePreviewProps> = ({
  guid,
  fileName,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const { fileBlobUrl, isLoading, error } = useFileByGuid(guid);
  const [imageError, setImageError] = useState(false);

  if (!isImageFileName(fileName)) {
    return null;
  }

  const handleToggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  // 画像を表示するボタン
  if (!isVisible) {
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "inline-flex items-center gap-2 text-blue-500 hover:text-blue-600",
          className,
        )}
        onClick={handleToggleVisibility}
      >
        <Image className="size-4" />
        {t("common.showImage")}
      </Button>
    );
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          "inline-flex items-center justify-center rounded bg-gray-100 p-1 text-xs text-gray-500",
          className,
        )}
      >
        {t("common.loading", "Loading...")}
      </div>
    );
  }

  if (error || !fileBlobUrl || imageError) {
    return (
      <div
        className={cn(
          "inline-flex items-center justify-center rounded bg-gray-100 p-1 text-xs text-gray-500",
          className,
        )}
      >
        {t("common.failedToLoadImage", "Failed to load image")}: {fileName}
      </div>
    );
  }

  return (
    <div className="inline-block">
      <img
        src={fileBlobUrl}
        alt={fileName}
        className={cn(
          "max-h-96 max-w-full rounded-md object-contain",
          className,
        )}
        onError={() => setImageError(true)}
        loading="lazy"
      />
    </div>
  );
};

// 画像参照を抽出するヘルパー関数
export const extractImageReferences = (
  content: string,
): Array<{ fileName: string; guid: string; fullMatch: string }> => {
  // パターン: [filename.ext](file-guid:guid "filename.ext")
  const imageRegex =
    /\[(.*?)\]\(file-guid:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\s+"(.*?)")?\)/g;

  const matches = [...content.matchAll(imageRegex)];
  return matches
    .map((match) => ({
      fileName: match[1] || match[3] || "File",
      guid: match[2],
      fullMatch: match[0],
    }))
    .filter(({ fileName }) => {
      // 画像拡張子を持つファイルのみを含める
      return isImageFileName(fileName);
    });
};
